/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { postAttendanceRecaptcha, getMember } from '../services';
import { getMemberNumber, getTags, tabulationCompanyProposalMapper } from '../utils';
import { LoVNode } from '../utils/types';
import useAuth from './useAuth';
import { postMapper, postMapperPRD } from './types';

const defaultValues = {
  firstName: '',
  email: '',
  phoneNumber: '',
  solicitationType: '',
  subject: '',
  message: '',
  lastName: '',
  voebizCode: '',
};

export type companyProposalData = typeof defaultValues & {
  attachments?: { fileName: string; contentType: string; data: string }[];
};

type useCompanyProposalProps = {
  list: LoVNode[];
  xRecaptcha: string;
};

export function useCompanyProposal({ list, xRecaptcha }: useCompanyProposalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const { apiDomain } = useFlags();
  const { isLogged } = useAuth();
  const token = localStorage.getItem('session-token');

  function postMapperValidation() {
    const url: string = apiDomain;
    if (url.includes('-hml') || url.includes('-dev')) {
      return postMapper;
    }
    return postMapperPRD;
  }

  const onSubmit = async (data: companyProposalData) => {
    setIsLoading(true);
    const userData = await getMember(apiDomain);
    function validateMemberNumber(memberNumber: string) {
      if (memberNumber) {
        return data.voebizCode;
      }
      if (isLogged) {
        return getMemberNumber(token);
      }
      return '';
    }

    try {
      const response = await postAttendanceRecaptcha({
        apiDomain,
        attendanceObj: {
          ticketInfo: {
            ticket: {
              subject: data.subject,
              description: data.solicitationType,
              comment: {
                body: `${data.message} \n ${!isLogged ? data.email : userData?.member.email}`,
                public: false,
              },
              ticket_form_id: 1900006384265,
              tags: [getTags(window.location.hostname)],
              author: {
                external_id: validateMemberNumber(data.voebizCode),
                name: isLogged
                  ? `${userData?.member.firstName} ${userData?.member.lastName}`
                  : `${data.firstName} ${data.lastName}`,
              },
              fields: [
                {
                  id: postMapperValidation().memberNumber,
                  value: validateMemberNumber(data.voebizCode),
                },
                { id: postMapperValidation().subChannel, value: 'formulario_clube_smiles_para_empresas' },
                {
                  id: postMapperValidation().phone1,
                  value: isLogged
                    ? `55${userData?.member.celPhoneDDDNumber1}${userData?.member.celPhoneNumber1}`
                    : `55${data.phoneNumber.replace(/[^0-9]+/gi, '')}`,
                },
                {
                  id: postMapperValidation().ticketChannel, // canal do ticket
                  value: 'voebiz',
                },
                {
                  id: postMapperValidation().ticketTabulation,
                  value: tabulationCompanyProposalMapper(list),
                },
                {
                  id: postMapperValidation().completeName,
                  value: !isLogged
                    ? `${data.firstName} ${data.lastName}`
                    : `${userData?.member.firstName} ${userData?.member.lastName}`,
                },
                {
                  id: postMapperValidation().email,
                  value: !isLogged ? data.email : userData?.member.email,
                },
              ],
              attachments: data.attachments,
            },
          },
        },
        xRecaptcha,
      });
      if (response === 'Ticket created in Zendesk') {
        setSuccess(true);
        return response;
      }
      if (response === 'Recaptcha error') {
        setError(true);
        return response;
      }
      throw new Error('We have a problem to send this request');
    } catch (e: any) {
      setError(true);
      return e;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    success,
    onSubmit,
    setError,
    setSuccess,
  };
}
